/**
 * Deterministically maps a category name to a colour from the given palette.
 * Uses a simple polynomial hash so the same category always gets the same colour.
 * Returns null when category is undefined/empty or the palette is empty.
 */
export function categoryColor(category: string | undefined, palette: string[]): string | null {
  if (!category || palette.length === 0) return null;
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    // >>> 0 keeps the value as an unsigned 32-bit integer, avoiding negatives.
    hash = (hash * 31 + category.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length];
}

/**
 * Returns true when a node should be rendered at full opacity given the active category filter.
 * - null activeCategories means "show all" (no filter active).
 * - Nodes with no primary category are always shown (uncategorised pages don't participate in the filter).
 */
export function isNodeActive(
  node: { categories?: string[] },
  activeCategories: Set<string> | null,
): boolean {
  if (activeCategories === null) return true;
  const primary = node.categories?.[0];
  if (!primary) return true;
  return activeCategories.has(primary);
}

/**
 * Extracts the sorted list of unique primary categories from a node array.
 */
export function extractCategories(nodes: { categories?: string[] }[]): string[] {
  const cats = new Set<string>();
  for (const node of nodes) {
    const primary = node.categories?.[0];
    if (primary) cats.add(primary);
  }
  return Array.from(cats).sort();
}
